/**
 * Pure builders for position protection overlays on the price chart.
 *
 * Extracted from Dashboard.jsx so the safety-critical part — telling an
 * operator whether a stop actually exists at the broker — is unit-testable
 * instead of buried in a 4,900-line component behind a useMemo.
 *
 * Core rule: intent and broker confirmation are DIFFERENT facts and must
 * never be rendered as one line. A position whose stop exists only inside
 * TradoPro is unprotected at the broker. That state went unnoticed for hours
 * on 2026-07-20, so it is drawn as a distinct dashed warning rather than
 * looking identical to a confirmed stop.
 */

const finiteOrNull = (...values) => {
  for (const value of values) {
    const numeric = Number(value);
    if (value !== null && value !== undefined && value !== "" && Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return null;
};

const positivePrice = (value) => (value !== null && value > 0 ? value : null);

/**
 * Classify a position's protection state from the broker-authoritative
 * fields already present in the /positions payload.
 */
export const resolveProtectionState = (position) => {
  const confirmedStop = positivePrice(
    finiteOrNull(position?.broker_confirmed_stop_price, position?.broker_stop_price),
  );
  const intendedStop = positivePrice(
    finiteOrNull(
      position?.intended_stop_price,
      position?.stop_loss,
      position?.stop_loss_price,
    ),
  );
  const hasBrokerOrder = Boolean(
    position?.broker_stop_order_id || position?.protective_stop_order_id,
  );
  // A price alone is not confirmation — the broker must hold a stop order.
  const brokerStopConfirmed = Boolean(confirmedStop !== null && hasBrokerOrder);

  const intendedDiffers =
    intendedStop !== null
    && (!brokerStopConfirmed
      || Math.abs(intendedStop - (confirmedStop ?? intendedStop)) > 1e-9);

  return {
    confirmedStop,
    intendedStop,
    brokerStopConfirmed,
    intendedDiffers,
    // The state that matters most: open exposure with nothing at the broker.
    unprotected: !brokerStopConfirmed,
  };
};

/**
 * Build chart overlays for one position's stop and target.
 * Returns [] when the position has no renderable protection levels.
 */
export const buildPositionProtectionOverlays = (position, firstTime, lastTime) => {
  if (!position || !firstTime || !lastTime) return [];

  const overlays = [];
  const { confirmedStop, intendedStop, brokerStopConfirmed, intendedDiffers } =
    resolveProtectionState(position);

  if (brokerStopConfirmed) {
    overlays.push({
      id: `position-stop-confirmed-${position.id}`,
      label: `${position.symbol} Stop (broker)`,
      color: "#f87171",
      lineWidth: 2,
      lineStyle: "solid",
      affectsScale: false,
      lastValueVisible: true,
      priceLineVisible: true,
      values: [
        { time: firstTime, value: confirmedStop },
        { time: lastTime, value: confirmedStop },
      ],
    });
  }

  if (intendedDiffers) {
    overlays.push({
      id: `position-stop-intended-${position.id}`,
      label: brokerStopConfirmed
        ? `${position.symbol} Stop (intended, differs)`
        : `${position.symbol} Stop (NOT AT BROKER)`,
      color: brokerStopConfirmed ? "#fbbf24" : "#fb7185",
      lineWidth: 2,
      lineStyle: "dashed",
      affectsScale: false,
      lastValueVisible: true,
      priceLineVisible: true,
      values: [
        { time: firstTime, value: intendedStop },
        { time: lastTime, value: intendedStop },
      ],
    });
  }

  const targetPrice = positivePrice(
    finiteOrNull(position?.take_profit, position?.take_profit_price),
  );
  if (targetPrice !== null) {
    const suppressed = Boolean(position?.take_profit_suppressed);
    overlays.push({
      id: `position-target-${position.id}`,
      label: suppressed
        ? `${position.symbol} Target (suppressed)`
        : `${position.symbol} Target`,
      color: suppressed ? "#65a30d" : "#4ade80",
      lineStyle: suppressed ? "dashed" : "solid",
      lineWidth: 1,
      affectsScale: false,
      lastValueVisible: true,
      priceLineVisible: true,
      values: [
        { time: firstTime, value: targetPrice },
        { time: lastTime, value: targetPrice },
      ],
    });
  }

  return overlays;
};

/** Build overlays for every position on the charted symbol. */
export const buildPositionChartData = (positions, firstTime, lastTime) => {
  if (!Array.isArray(positions) || !positions.length || !firstTime || !lastTime) {
    return { overlays: [], markers: [] };
  }
  const overlays = [];
  positions.forEach((position) => {
    overlays.push(...buildPositionProtectionOverlays(position, firstTime, lastTime));
  });
  return { overlays, markers: [] };
};
